import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLocalContext } from "../../context/LocalContext";
import "./Contact.css";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65 } },
};

const fade = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.65 } },
};

const Contact = () => {
  const { webinfo = {} } = useLocalContext();

  const phoneCall = webinfo?.phonecall || "";
  const phoneText = webinfo?.phone || webinfo?.phonecall || "See email";
  const email = webinfo?.email || "contact@smartprintersolutions.com";
  const address = webinfo?.address || "2100 Ross Avenue, Suite 2700, Dallas, TX 75201";

  const mapSrc = useMemo(
    () =>
      "https://maps.google.com/maps?q=2100+Ross+Avenue,+Dallas,+TX+75201&hl=en&z=16&output=embed",
    []
  );

  const [loading, setLoading] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    setTimeout(() => {
      setLoading(false);
      alert(
        "Thank you. Your message was received. We typically respond within one to two business days. Marketing outcomes depend on many factors—we will not promise specific rankings or ROI in a first reply."
      );
      e.target.reset();
    }, 600);
  };

  return (
    <section className="contact" aria-label="Contact">
      <div className="contact__wrap">
        <motion.div
          className="contact__head"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.45 }}
        >
          <div className="contact__tag">
            <span className="dot" aria-hidden="true" />
            Contact
          </div>

          <h1 className="contact__title">
            Let&apos;s talk about your <span>digital marketing</span>.
          </h1>

          <p className="contact__sub">
            Use this form for new projects, RFPs, or questions about our services. For legal or privacy requests, email
            us directly and reference our Privacy Policy.
          </p>
        </motion.div>

        <div className="contact__grid">
          <motion.aside
            className="contact__card contact__card--soft"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="contact__h2">Reach us</h2>
            <p className="contact__p">
              If you need an NDA before sharing details, mention that in your message.
            </p>

            <ul className="contact__chips" aria-label="Highlights">
              <li>1–2 business day replies</li>
              <li>Clear scoping next steps</li>
              <li>No obligation intro call</li>
              <li>Written proposals</li>
            </ul>

            <ul className="contact__list" aria-label="Contact details">
              <li className="contact__item">
                <i className="contact__icon" aria-hidden="true">
                  📞
                </i>
                <div>
                  <p className="contact__label">Phone</p>
                  <p className="contact__value">
                    {phoneCall ? <a href={`tel:${phoneCall}`}>{phoneText}</a> : <span>{phoneText}</span>}
                  </p>
                </div>
              </li>

              <li className="contact__item">
                <i className="contact__icon" aria-hidden="true">
                  ✉️
                </i>
                <div>
                  <p className="contact__label">Email</p>
                  <p className="contact__value">
                    <a href={`mailto:${email}`}>{email}</a>
                  </p>
                </div>
              </li>

              <li className="contact__item">
                <i className="contact__icon" aria-hidden="true">
                  📍
                </i>
                <div>
                  <p className="contact__label">Location</p>
                  <p className="contact__value">{address}</p>
                </div>
              </li>

              <li className="contact__item">
                <i className="contact__icon" aria-hidden="true">
                  ⏱
                </i>
                <div>
                  <p className="contact__label">Hours</p>
                  <p className="contact__value">Monday–Friday · 9:00 AM – 5:00 PM (Central Time, excluding holidays)</p>
                </div>
              </li>
            </ul>
          </motion.aside>

          <motion.div
            className="contact__card"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="contact__h2">Project inquiry</h2>
            <p className="contact__p">
              Include your website, primary goal (leads, sales, awareness), and monthly media budget if you have one. The
              more context you share, the more useful our reply.
            </p>

            <form className="contact__form" onSubmit={onSubmit}>
              <div className="contact__row">
                <div className="contact__field">
                  <label htmlFor="contact-name">Your name</label>
                  <input
                    id="contact-name"
                    className="contact__input"
                    type="text"
                    name="name"
                    placeholder="Alex Rivera"
                    autoComplete="name"
                    required
                  />
                </div>

                <div className="contact__field">
                  <label htmlFor="contact-email">Work email</label>
                  <input
                    id="contact-email"
                    className="contact__input"
                    type="email"
                    name="email"
                    placeholder="you@company.com"
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className="contact__row">
                <div className="contact__field">
                  <label htmlFor="contact-service">Service interest</label>
                  <select className="contact__select" id="contact-service" name="service" defaultValue="Strategy">
                    <option>Strategy &amp; consulting</option>
                    <option>SEO &amp; content</option>
                    <option>Paid search / social</option>
                    <option>Social media management</option>
                    <option>Email &amp; lifecycle</option>
                    <option>Analytics &amp; CRO</option>
                    <option>Full-funnel / not sure</option>
                  </select>
                </div>

                <div className="contact__field">
                  <label htmlFor="contact-budget">Monthly marketing budget (optional)</label>
                  <select className="contact__select" id="contact-budget" name="budget" defaultValue="Prefer not to say">
                    <option>Prefer not to say</option>
                    <option>Under $2,500</option>
                    <option>$2,500 – $7,500</option>
                    <option>$7,500 – $20,000</option>
                    <option>$20,000+</option>
                  </select>
                </div>
              </div>

              <div className="contact__field">
                <label htmlFor="contact-site">Website (optional)</label>
                <input
                  id="contact-site"
                  className="contact__input"
                  type="url"
                  name="website"
                  placeholder="https://example.com"
                />
              </div>

              <div className="contact__field">
                <label htmlFor="contact-message">How can we help?</label>
                <textarea
                  id="contact-message"
                  className="contact__textarea"
                  name="message"
                  placeholder="Goals, timeline, current channels, and anything we should know about compliance (regulated industry, etc.)."
                  required
                />
              </div>

              <div className="contact__actions">
                <button className="contact__btn contact__btn--primary" type="submit" disabled={loading}>
                  {loading ? "Sending..." : "Send message"} <span aria-hidden="true">→</span>
                </button>

                <a className="contact__btn contact__btn--ghost" href={`mailto:${email}`}>
                  Email instead
                </a>

                <p className="contact__fineprint">
                  By submitting, you agree we may contact you about this inquiry and that our{" "}
                  <a href="/privacy-policy">Privacy Policy</a> applies.
                </p>
              </div>
            </form>
          </motion.div>
        </div>

        <div className="contact__bottom">
          <motion.div
            className="contact__map"
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <iframe title="Map overview" src={mapSrc} loading="lazy" allowFullScreen />
          </motion.div>

          <motion.div
            className="contact__faq"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <div className="contact__qa">
              <h3>Do you guarantee leads or rankings?</h3>
              <p>
                No. Search engines and ad auctions control outcomes. We set up tests, optimize responsibly, and report
                honestly.
              </p>
            </div>

            <div className="contact__qa">
              <h3>Do you work internationally?</h3>
              <p>
                Yes, when it is a fit. Timelines, creative, and compliance can vary by region; we adapt where needed.
              </p>
            </div>

            <div className="contact__qa">
              <h3>Will you sign an NDA?</h3>
              <p>
                Often yes for qualified opportunities. Mention it in your message and we will send a mutual NDA or
                review yours.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
